import {
  GenerationProgress,
  type GenerationStep,
  useTimedStep,
} from "@rosc/ui/v32/generation-progress";
import { t } from "i18next";
import { useEffect, useRef } from "react";
import { StatusFile } from "@/common/enums/enums";
import { AnimatedText } from "@/components/ui/AnimatedText";
import { Alert } from "@/components/ui/alert";
import { FileResultRendererMD } from "@/components/ui/visits/file-result-renderer-md";
import { useSummarizationSocket } from "@/hooks/summarization_realtime/useSummarizationSocket";
import { toast } from "@/hooks/use-toast";
import { documentTemplateName } from "@/lib/document-name";
import { noteOptionLabel } from "@/lib/note-options";
import { hasMainRecording } from "@/lib/pre-chart";
import type { FileData } from "@/services/files";

export const isNoteGenerating = (file: Pick<FileData, "status">): boolean =>
  file.status === StatusFile.GENERATING.toString() ||
  file.status === StatusFile.PROCESSING.toString();

// Reads the persisted main note while generating. Shared by the classic
// note card and the redesigned workspace.
export const useNoteGenerationStream = (
  file: Pick<FileData, "public_id" | "status">,
) => {
  const { result, isLoading, error } = useSummarizationSocket(file);
  const previous = useRef({ publicId: file.public_id, isLoading: false });

  useEffect(() => {
    if (
      previous.current.publicId === file.public_id &&
      previous.current.isLoading &&
      result &&
      !error &&
      !isLoading
    ) {
      toast({
        variant: "success",
        title: t(
          "visit.components.cardContentTemplateGenerating.noteGeneratedSuccessfully",
        ),
        duration: 1000,
      });
    }
    previous.current = { publicId: file.public_id, isLoading };
  }, [file.public_id, isLoading, error, result]);

  return {
    isThinking: !result && isLoading,
    result,
    error,
    isFinished: !!result && !error && !isLoading,
  };
};

export type NoteGenerationStream = ReturnType<typeof useNoteGenerationStream>;

// A pre-charted visit being merged: its recordings were added with Start
// visit, so the visit has no recording of its own, and the note on screen is
// still the pre-chart until the final note is written (clinical lead,
// 01/11/2026: it looked frozen). Ordinary visits always have a duration.
export const isPreChartMerging = (
  file: Pick<FileData, "status" | "transcription" | "duration">,
): boolean => isNoteGenerating(file) && !hasMainRecording(file);

// What the note's progress shows. Processing (the recording being
// transcribed) and Generating (the note being written) are real stages of
// the visit; inside Generating the worker reports nothing more, so reading,
// writing and checking advance on time (3 s, then 20 s) and "Checking
// values…" stays current until the note is ready. Nothing ever shows as done
// before the note arrives.
export const NOTE_STEP_DELAYS = [3000, 20000] as const;
export const noteGenerationSteps = (): GenerationStep[] => [
  { key: "transcribing", label: t("generationProgress.steps.transcribing") },
  { key: "reading", label: t("generationProgress.steps.reading") },
  { key: "writing", label: t("generationProgress.steps.writingNote") },
  { key: "checking", label: t("generationProgress.steps.checking") },
];
export const useNoteGenerationStep = (
  file: Pick<FileData, "public_id" | "status">,
): number => {
  const writing = file.status === StatusFile.GENERATING.toString();
  const timed = useTimedStep(NOTE_STEP_DELAYS, writing, file.public_id);
  return writing ? 1 + timed : 0;
};

// The note's template and options, as in the visit header.
const noteSettings = (file: FileData): string =>
  [
    documentTemplateName(file),
    ...[file.style, file.format, file.exam]
      .filter((value): value is string => Boolean(value))
      .map(noteOptionLabel),
  ]
    .filter(Boolean)
    .join(" · ");

// Shown for the whole wait, above the note being replaced (dimmed and
// inert): a new note, a regenerated one, or a visit merging into its
// pre-chart (with its own wording: the final note replaces the pre-chart).
export const NoteGenerationProgress = ({
  file,
}: {
  file: FileData;
}): React.ReactNode => {
  const current = useNoteGenerationStep(file);
  const merging = isPreChartMerging(file);
  return (
    <GenerationProgress
      className="mb-4"
      current={current}
      description={merging ? t("preChart.merge.hint") : noteSettings(file)}
      steps={noteGenerationSteps()}
      testId={merging ? "pre-chart-merging" : "note-generating"}
      title={
        merging ? t("preChart.merge.title") : t("generationProgress.note.title")
      }
    />
  );
};

export const NoteGeneratingBody = ({
  file,
  stream,
}: {
  file: FileData;
  stream: NoteGenerationStream;
}): React.ReactNode => {
  const merging = isPreChartMerging(file);
  const waiting = !stream.error && !stream.isFinished;
  return (
    <>
      {waiting && <NoteGenerationProgress file={file} />}
      {stream.error && <Alert variant="destructive">{stream.error}</Alert>}
      {waiting && stream.result ? (
        // The note being replaced (the pre-chart, or the note before a
        // regenerate): still readable, clearly not the new note, and
        // nothing in it can be selected or clicked.
        <div
          aria-busy
          className="pointer-events-none select-none opacity-45 transition-opacity"
          data-testid={
            merging ? "pre-chart-merging-note" : "note-generating-previous"
          }
          inert
        >
          <FileResultRendererMD result={stream.result} />
        </div>
      ) : (
        <FileResultRendererMD result={stream.result || ""} />
      )}
      {!merging && stream.isFinished && (
        <AnimatedText className="mt-2" duration={2} variant="fade">
          {t(
            "visit.components.cardContentTemplateGenerating.textGeneratedSuccessfully",
          )}
        </AnimatedText>
      )}
    </>
  );
};
